import {
  eventListener,
  IPropertySelection,
  type ISubscribeSelectedProperty,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import { useEffect, useRef, useState } from "react";
import { copyPropertySelection } from "../properties/utils";
import {
  readPropertyFromStorage,
  writePropertyToStorage,
} from "./UserContextService";

/**
 * Holds and returns state for a property selection.
 * Listens for SUBSCRIBE_SELECTED_PROPERTY events, maintains a list of subscribers and calls them on property selection
 * changes.
 * Sets and retrieves the property selection from local storage.
 */
export const usePropertySelectorListener = (
  properties: IPropertySelection[] | undefined
): {
  property: IPropertySelection | null;
  setProperty: (value: IPropertySelection | null) => void;
} => {
  const [property, setProperty] = useState<IPropertySelection | null>(null);
  const propertyRef = useRef<IPropertySelection | null>(null);
  const subscribersList = useRef<
    ((property: IPropertySelection | null) => IPropertySelection | null)[]
  >([]);

  // on mount, listen for property selection subscribers
  useEffect(() => {
    const handleSubscribeProperty = (event: ISubscribeSelectedProperty) => {
      const { subscriber } = event.payload;
      subscriber(copyPropertySelection(propertyRef.current));
      subscribersList.current.push(subscriber);
      return () =>
        subscribersList.current.splice(
          subscribersList.current.indexOf(subscriber),
          1
        );
    };
    return eventListener.addListener(
      TOPICS.SUBSCRIBE_SELECTED_PROPERTY,
      handleSubscribeProperty
    );
  }, []);

  // when properties become available, load the selection from storage
  useEffect(() => {
    if (properties) {
      const loadedProperty = readPropertyFromStorage();
      if (
        !loadedProperty ||
        properties.some((prop) => prop.id === loadedProperty?.id)
      ) {
        setProperty(loadedProperty);
      }
    }
  }, [properties]);

  // when the selection changes notify the subscribers
  useEffect(() => {
    propertyRef.current = property;
    subscribersList.current.forEach((subscriber) => {
      subscriber(copyPropertySelection(propertyRef.current));
    });
  }, [property]);

  // when a selection is made, write it to storage
  const setPropertyHandler = (value: IPropertySelection | null) => {
    writePropertyToStorage(value);
    setProperty(value);
  };

  return { property, setProperty: setPropertyHandler };
};
